import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { getUsers, getDailyByDate } from "../../services/newdaily";
import UserCard from "../../components/UserCard/UserCard";
import UserCardHeader from "../../components/UserCardHeader/UserCardHeader";
import TaskHeaderAdmin from "../../components/TaskHeaderAdmin/TaskHeaderAdmin";
import TaskCard from "../../components/TaskCard/TaskCard";
import TimeBar from "../../components/TimeBar/TimeBar";
import foldersImg from "../../assets/images/folders.png";
import FilterBar from "../../components/FilterBar/FilterBar";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import styles from "./AdminMobile.module.css";

import FormCheckIcon from "../../assets/svgs/formcheck.svg";
import CloseIcon from "../../assets/svgs/close.svg";

const AdminMobile = ({ empleado, refreshSignal, isMobile }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allTasksBase, setAllTasksBase] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const fechaParam = searchParams.get("date");

    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      return fechaParam;
    }

    return moment().format("YYYY-MM-DD");
  });
  const [notificationMessage, setNotificationMessage] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [orderOption, setOrderOption] = useState("priority");
  const [activeCounter, setActiveCounter] = useState({ userId: null, type: null });
  const [showModal, setShowModal] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const userListRef = useRef(null);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [queryTaskId, setQueryTaskId] = useState(null);
  const [queryHighlightType, setQueryHighlightType] = useState(null);
  const [initialDepartmentFromQuery, setInitialDepartmentFromQuery] = useState(null);
  const [pendingAutoOpenReassignTaskId, setPendingAutoOpenReassignTaskId] = useState(null);

  const hasAppliedInitialParams = useRef(false);
  const pendingEmployeeFromQueryRef = useRef(null);
  const shouldOpenUserModalFromQueryRef = useRef(false);
  const shouldAutoOpenReassignFromQueryRef = useRef(false);

  const hasLockedUserFromQueryRef = useRef(false);

  const usersSpinnerTimeoutRef = useRef(null);

  const getFirstDepartment = (user) => {
    if (!user?.Departamentos) return null;

    if (Array.isArray(user.Departamentos)) {
      return user.Departamentos[0] || null;
    }

    if (typeof user.Departamentos === "string") {
      return user.Departamentos || null;
    }

    return null;
  };

  const handleToggleCounter = (userId, type, willBeActive) => {
    setActiveCounter(willBeActive ? { userId, type } : { userId: null, type: null });
  };

  const fetchUsers = async () => {
    try {
      if (usersSpinnerTimeoutRef.current) clearTimeout(usersSpinnerTimeoutRef.current);

      usersSpinnerTimeoutRef.current = setTimeout(() => {
        setIsLoadingUsers(true);
      }, 1000);

      const data = await getUsers();

      if (data?.Usuarios && Array.isArray(data.Usuarios)) {
        setUsers(data.Usuarios);
        setFilteredUsers(data.Usuarios);

        if (
          !pendingEmployeeFromQueryRef.current &&
          !hasLockedUserFromQueryRef.current &&
          !data.Usuarios.some((u) => String(u.id) === String(selectedUserId))
        ) {
          setSelectedUserId(data.Usuarios[0]?.id ?? null);
        }

        if (data.Usuarios.length === 0) {
          setAllTasksBase([]);
          setActiveCounter({ userId: null, type: null });
        }
      } else {
        console.error("Respuesta inesperada de getUsers.php:", data);
        setUsers([]);
        setFilteredUsers([]);
        setSelectedUserId(null);
        setAllTasksBase([]);
        setActiveCounter({ userId: null, type: null });
      }
    } catch (e) {
      console.error("Error al cargar usuarios:", e);
      setUsers([]);
      setFilteredUsers([]);
      setSelectedUserId(null);
      setAllTasksBase([]);
      setActiveCounter({ userId: null, type: null });
    } finally {
      if (usersSpinnerTimeoutRef.current) {
        clearTimeout(usersSpinnerTimeoutRef.current);
        usersSpinnerTimeoutRef.current = null;
      }
      setIsLoadingUsers(false);
    }
  };

  const fetchAllUsersTasks = async (date) => {
    if (!users.length) {
      setAllTasksBase([]);
      return;
    }

    try {
      setIsLoadingTasks(true);

      const data = await getDailyByDate({ fecha: date });

      if (data?.Daily || data?.future_tasks) {
        const tareaDefs = data.Tareas || [];
        const proyectoDefs = data.Proyectos || [];

        const dailyList = Array.isArray(data?.Daily) ? data.Daily : [];
        const futureList = Array.isArray(data?.future_tasks) ? data.future_tasks : [];
        const mergedTasks = [...dailyList, ...futureList];

        const tasksOfSelectedDay = mergedTasks.filter(
          (task) =>
            moment(task.Fecha).format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD")
        );

        const allTasksArray = tasksOfSelectedDay
          .filter((task) => task.Automatica !== 1)
          .map((task) => {
            const startDate = task.Inicio
              ? moment(`${task.Fecha} ${task.Inicio}`, "YYYY-MM-DD HH:mm:ss").toDate()
              : moment(task.Fecha, "YYYY-MM-DD").startOf("day").toDate();

            const endDate = task.Inicio
              ? moment(startDate).add(task.Minutos || 0, "minutes").toDate()
              : startDate;

            let tareaName = task.Tarea || "";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea) tareaName = foundTarea.tarea;
            }

            let subtareaName = "";
            const isOtra =
              task.Tarea === "Otra" ||
              String(task.IDTarea ?? "") === "-1" ||
              tareaName === "Otra";

            if (isOtra) {
              subtareaName = String(
                task.Otra ??
                task.Subtarea ??
                task.SubtareaTexto ??
                task.subtarea ??
                ""
              ).trim();
            } else if (task.IDTarea && task.IDSubtarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea && Array.isArray(foundTarea.subtareas)) {
                const foundSub = foundTarea.subtareas.find(
                  (s) => s.id.toString() === task.IDSubtarea.toString()
                );
                if (foundSub) subtareaName = foundSub.subtarea;
              }
            } else {
              subtareaName = task.Subtarea || "";
            }

            let proyectoName = task.Proyecto || "";
            if (task.IProyecto) {
              const foundProyecto = proyectoDefs.find(
                (p) => p.id.toString() === task.IProyecto.toString()
              );
              if (foundProyecto) proyectoName = foundProyecto.Proyecto;
            }

            let taskColor = task.Color || "#ccc";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea?.color) {
                taskColor = foundTarea.color.startsWith("#")
                  ? foundTarea.color
                  : `#${foundTarea.color}`;
              }
            }

            return {
              id: task.id,
              start: startDate,
              end: endDate,
              color: taskColor,
              date: moment(task.Fecha, "YYYY-MM-DD").format("YYYY-MM-DD"),
              Minutos: task.Minutos || 0,
              Tarea: tareaName,

              Subtarea: subtareaName,
              subtarea: subtareaName,
              subtask: subtareaName,

              Proyecto: proyectoName,
              Inicio: task.Inicio || null,
              Hecho: task.Hecho || 0,
              IDOrigen: task.IDOrigen ?? 0,
              Origen: task.Origen || null,
              Empleado: task.Empleado,
              Notas: task.Notas,
              Descripcion: task.Descripcion,

              is_projection: !!task.is_projection,
              parent_periodic_id: task.parent_periodic_id ?? null,
            };
          });

        const grouped = users.map((u) => {
          const tasks = allTasksArray.filter(
            (t) => t.Empleado.toString() === u.id.toString()
          );
          return { user: u, tasks };
        });

        setAllTasksBase(grouped);
      } else {
        setAllTasksBase([]);
      }
    } catch (e) {
      console.error("Error al cargar tareas para todos los usuarios:", e);
      setAllTasksBase([]);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const reorderAllTasks = (list, order) => {
    const newList = [...list];
    newList.sort((a, b) => {
      const warningsA = a.tasks.filter((t) => t.IDOrigen < 0).length;
      const warningsB = b.tasks.filter((t) => t.IDOrigen < 0).length;
      const notesA = a.tasks.filter((t) => (t.Notas ?? "").trim() !== "").length;
      const notesB = b.tasks.filter((t) => (t.Notas ?? "").trim() !== "").length;
      const totalA = a.tasks.reduce((sum, t) => sum + (t.Minutos || 0), 0);
      const totalB = b.tasks.reduce((sum, t) => sum + (t.Minutos || 0), 0);

      switch (order) {
        case "priority":
          if (warningsA !== warningsB) return warningsB - warningsA;
          if (totalA !== totalB) return totalB - totalA;
          return a.user.Nombre.localeCompare(b.user.Nombre);
        case "maxLoad":
          if (totalA !== totalB) return totalB - totalA;
          return a.user.Nombre.localeCompare(b.user.Nombre);
        case "minLoad":
          if (totalA !== totalB) return totalA - totalB;
          return a.user.Nombre.localeCompare(b.user.Nombre);
        case "alphabetical":
          return a.user.Nombre.localeCompare(b.user.Nombre);
        case "notesCount":
          if (notesA !== notesB) return notesB - notesA;
          return a.user.Nombre.localeCompare(b.user.Nombre);
        default:
          if (totalA !== totalB) return totalB - totalA;
          return a.user.Nombre.localeCompare(b.user.Nombre);
      }
    });
    return newList;
  };

  const allTasks = useMemo(() => {
    return reorderAllTasks(allTasksBase, orderOption);
  }, [allTasksBase, orderOption]);

  useEffect(() => {
    fetchUsers(selectedDate);
  }, [selectedDate, refreshSignal]);

  useEffect(() => {
    if (allTasks.length > 0) {
      setAllTasksBase((prev) => reorderAllTasks(prev, orderOption));
    }
  }, [orderOption]);

  const handleTaskSuccess = (message) => {
    setNotificationMessage(message);
    setTimeout(() => {
      setNotificationMessage("");
    }, 3000);
  };

  useEffect(() => {
    const fechaParam = searchParams.get("date");
    const empleadoParam = searchParams.get("empleado");
    const taskIdParam = searchParams.get("task_id");
    const notesParam = searchParams.get("notes");

    const shouldOpenNotes = notesParam === "true";

    let usedParams = false;

    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      setSelectedDate(fechaParam);
      usedParams = true;
    }

    if (empleadoParam) {
      pendingEmployeeFromQueryRef.current = String(empleadoParam);
      usedParams = true;
    }

    if (shouldOpenNotes) {
      setQueryHighlightType("notes");
      setQueryTaskId(null);

      shouldOpenUserModalFromQueryRef.current = true;
      shouldAutoOpenReassignFromQueryRef.current = false;
      usedParams = true;
    } else if (taskIdParam) {
      const taskId = String(taskIdParam);

      setQueryTaskId(taskId);
      setPendingAutoOpenReassignTaskId(taskId);
      setQueryHighlightType(null);

      shouldOpenUserModalFromQueryRef.current = true;
      shouldAutoOpenReassignFromQueryRef.current = true;
      usedParams = true;
    } else {
      setQueryTaskId(null);
      setPendingAutoOpenReassignTaskId(null);
      setQueryHighlightType(null);
      shouldOpenUserModalFromQueryRef.current = false;
      shouldAutoOpenReassignFromQueryRef.current = false;
    }

    if (usedParams && !hasAppliedInitialParams.current) {
      hasAppliedInitialParams.current = true;
      navigate(".", { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!users.length || !pendingEmployeeFromQueryRef.current) return;

    const employeeIdFromQuery = pendingEmployeeFromQueryRef.current;

    const foundUser = users.find(
      (u) => String(u.id) === String(employeeIdFromQuery)
    );

    if (!foundUser) {
      pendingEmployeeFromQueryRef.current = null;
      shouldOpenUserModalFromQueryRef.current = false;
      shouldAutoOpenReassignFromQueryRef.current = false;
      hasLockedUserFromQueryRef.current = false;
      return;
    }

    const userDepartment = getFirstDepartment(foundUser);
    if (userDepartment) {
      setInitialDepartmentFromQuery(userDepartment);
    }

    hasLockedUserFromQueryRef.current = true;
    setSelectedUserId(foundUser.id);

    if (shouldOpenUserModalFromQueryRef.current) {
      setShowModal(true);
    }
  }, [users]);

  useEffect(() => {
    if (!pendingEmployeeFromQueryRef.current || selectedUserId == null) return;

    if (String(selectedUserId) === String(pendingEmployeeFromQueryRef.current)) {
      pendingEmployeeFromQueryRef.current = null;
      shouldOpenUserModalFromQueryRef.current = false;
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (!selectedUserId || !queryHighlightType) return;
    if (!allTasks.length) return;
    if (!showModal) return;

    setActiveCounter({
      userId: selectedUserId,
      type: queryHighlightType,
    });

    setQueryHighlightType(null);
  }, [selectedUserId, queryHighlightType, allTasks, showModal]);

  useEffect(() => {
    return () => {
      if (usersSpinnerTimeoutRef.current) clearTimeout(usersSpinnerTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const vp = userListRef.current?.osInstance()?.elements().viewport;
    if (vp) vp.scrollTop = 0;
  }, [orderOption]);

  useEffect(() => {
    if (!allTasks.length) return;
    if (pendingEmployeeFromQueryRef.current) return;
    if (hasLockedUserFromQueryRef.current) return;

    const visibles = allTasks.filter((item) =>
      filteredUsers.some((u) => u.id === item.user.id)
    );

    if (!visibles.length) {
      if (selectedUserId !== null) setSelectedUserId(null);
      return;
    }

    const isCurrentVisible = visibles.some(
      (v) => String(v.user.id) === String(selectedUserId)
    );

    if (selectedUserId == null || !isCurrentVisible) {
      setSelectedUserId(visibles[0].user.id);
    }
  }, [allTasks, filteredUsers, selectedUserId]);

  useEffect(() => {
    fetchAllUsersTasks(selectedDate);
  }, [users, selectedDate, refreshSignal, orderOption]);

  const handleSelectUser = (user) => {
    hasLockedUserFromQueryRef.current = false;
    setSelectedUserId(user.id);
    setShowModal(true);
  };

  useEffect(() => {
    const body = document.body;

    if (showModal && !reassignModalOpen) {
      body.classList.add("modal-open");
    } else {
      body.classList.remove("modal-open");
    }
  }, [showModal, reassignModalOpen]);

  const selectedUserTasks =
    allTasks.find((g) => g.user.id === selectedUserId)?.tasks || [];

  const orderedTasks = useMemo(() => {
    const userTasks = allTasks.find((g) => g.user.id === selectedUserId)?.tasks || [];

    if (!activeCounter.type) {
      return [...userTasks].sort((a, b) => {
        if (a.Hecho !== b.Hecho) return a.Hecho - b.Hecho;

        const warnA = a.IDOrigen < 0;
        const warnB = b.IDOrigen < 0;
        if (warnA !== warnB) return warnA ? -1 : 1;

        if (a.sortStart && b.sortStart) {
          const diff = a.sortStart - b.sortStart;
          if (diff) return diff;
        }

        return a.Tarea.localeCompare(b.Tarea);
      });
    }

    return [...userTasks].sort((a, b) => {
      const isAMatch = activeCounter.type === "warning"
        ? a.IDOrigen < 0
        : a.Notas?.trim() !== "";
      const isBMatch = activeCounter.type === "warning"
        ? b.IDOrigen < 0
        : b.Notas?.trim() !== "";

      if (isAMatch !== isBMatch) return isBMatch - isAMatch;

      if (a.Hecho !== b.Hecho) return a.Hecho - b.Hecho;

      return a.Tarea.localeCompare(b.Tarea);
    });
  }, [allTasks, selectedUserId, activeCounter]);

  useEffect(() => {
    if (!showModal) return;
    if (reassignModalOpen) return;
    if (!shouldAutoOpenReassignFromQueryRef.current) return;
    if (!pendingAutoOpenReassignTaskId) return;
    if (!orderedTasks.length) return;

    const targetExists = orderedTasks.some(
      (t) => String(t.id) === String(pendingAutoOpenReassignTaskId)
    );

    if (!targetExists) return;

    const timer = setTimeout(() => {
      setQueryTaskId(String(pendingAutoOpenReassignTaskId));
    }, 0);

    return () => clearTimeout(timer);
  }, [
    showModal,
    reassignModalOpen,
    orderedTasks,
    pendingAutoOpenReassignTaskId,
  ]);

  const handleCloseBanner = () => setNotificationMessage("");

  return (
    <div className={styles.adminViewContainer}>
      <div className={styles.leftPanel}>
        <UserCardHeader
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          orderOption={orderOption}
          onOrderChange={setOrderOption}
          empleado={empleado}
          isMobile={isMobile}
        />
        <FilterBar
          orderOption={orderOption}
          onOrderChange={(newOrder) => setOrderOption(newOrder)}
          users={users}
          onFilteredUsersChange={(filtered) => setFilteredUsers(filtered)}
          initialDepartment={initialDepartmentFromQuery}
          isMobile={isMobile}
        />
        <OverlayScrollbarsComponent
          ref={userListRef}
          options={{
            scrollbars: { autoHide: "scroll", autoHideDelay: 300 },
            overflowBehavior: showModal ? { x: "hidden", y: "hidden" } : { x: "scroll", y: "scroll" },
          }}
          style={{ maxHeight: "84vh", width: "100%" }}
        >
          {isLoadingUsers ? (
            <div className={styles.loadingInScroll}>
              <div className={styles.spinner} />
              <p className={styles.loadingText}>Cargando</p>
            </div>
          ) : (
            <div className={`${styles.userList} ${showModal ? styles.scrollBlocked : ""}`}>
              {allTasks
                .filter((g) => filteredUsers.some((u) => u.id === g.user.id))
                .map((g) => (
                  <UserCard
                    key={g.user.id}
                    user={g.user}
                    tasks={g.tasks}
                    selectedDate={selectedDate}
                    onSelectUser={handleSelectUser}
                    isSelected={g.user.id === selectedUserId}
                    admin
                    isMobile
                    activeCounter={activeCounter.userId === g.user.id && activeCounter.type}
                    onToggleCounter={(type, active) =>
                      handleToggleCounter(g.user.id, type, active)
                    }
                  />
                ))}
            </div>
          )}
        </OverlayScrollbarsComponent>
      </div>

      {showModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowModal(false)}
        >
          <div
            className={`${styles.modalContent} ${isMobile ? styles.modalContentMobile : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            {isMobile ? (
              <>
                {!reassignModalOpen && (
                  <div className={styles.fixedHeaderMobile}>
                    <div className={styles.headerRowMobile}>
                      <TaskHeaderAdmin
                        userName={users.find((u) => u.id === selectedUserId)?.Nombre || ""}
                        selectedDate={selectedDate}
                        isMobile={true}
                      />
                      <button
                        className={styles.modalCloseMobile}
                        onClick={() => setShowModal(false)}
                        aria-label="Cerrar"
                      >
                        <img src={CloseIcon} alt="Cerrar" className={styles.closeIconMobile} />
                      </button>
                    </div>

                    <UserCard
                      user={users.find((u) => u.id === selectedUserId)}
                      tasks={selectedUserTasks}
                      selectedDate={selectedDate}
                      isSelected={true}
                      onSelectUser={() => { }}
                      isMobile={true}
                      isModal={true}
                      activeCounter={activeCounter.userId === selectedUserId && activeCounter.type}
                      onToggleCounter={(type, active) =>
                        handleToggleCounter(selectedUserId, type, active)
                      }
                    />
                  </div>
                )}
                <div className={styles.adminContentMobile}>
                  {notificationMessage && (
                    <div className={styles.notificationBanner}>
                      <img src={FormCheckIcon} alt="Check" className={styles.iconCheck} />
                      {notificationMessage}
                      <button
                        className={styles.closeButton}
                        onClick={handleCloseBanner}
                      >
                        <img src={CloseIcon} alt="Cerrar" className={styles.closeIcon} />
                      </button>
                    </div>
                  )}

                  <div className={styles.adminTaskList}>
                    {orderedTasks.length > 0 ? (
                      orderedTasks.map((t) => (
                        <TaskCard
                          key={t.id}
                          task={t}
                          user={users.find((u) => u.id === selectedUserId)}
                          onSuccess={handleTaskSuccess}
                          admin
                          allUsers={users}
                          allTasks={allTasks}
                          selectedDate={selectedDate}
                          empleado={empleado}
                          isMobile={true}
                          isModal={true}
                          onToggleReassignModal={setReassignModalOpen}
                          activeCounterType={
                            activeCounter.userId === selectedUserId
                              ? activeCounter.type
                              : null
                          }
                          autoOpenReassignTaskId={
                            String(t.id) === String(queryTaskId) ? queryTaskId : null
                          }
                          onAutoOpenReassignConsumed={(openedTaskId) => {
                            if (String(openedTaskId) === String(queryTaskId)) {
                              setQueryTaskId(null);
                              setPendingAutoOpenReassignTaskId(null);
                              shouldAutoOpenReassignFromQueryRef.current = false;
                            }
                          }}
                        />
                      ))
                    ) : (
                      <div className={styles.noTaskContainer}>
                        <img src={foldersImg} alt="No tasks" className={styles.noTaskImage} />
                        <p className={styles.noTaskText}>Este usuario no tiene tareas asignadas.</p>
                      </div>
                    )}
                    <div style={{ height: "24px" }} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <button
                  className={styles.modalClose}
                  onClick={() => setShowModal(false)}
                >
                  <img src={CloseIcon} alt="Cerrar" />
                </button>

                <TaskHeaderAdmin
                  userName={users.find((u) => u.id === selectedUserId)?.Nombre || ""}
                  selectedDate={selectedDate}
                  isMobile={false}
                />

                <div className={styles.adminContent}>
                  {notificationMessage ? (
                    <div className={styles.notificationBanner}>
                      <img src={FormCheckIcon} alt="Check" className={styles.iconCheck} />
                      {notificationMessage}
                      <button
                        className={styles.closeButton}
                        onClick={handleCloseBanner}
                      >
                        <img src={CloseIcon} alt="Cerrar" className={styles.closeIcon} />
                      </button>
                    </div>
                  ) : (
                    <TimeBar
                      tasks={selectedUserTasks}
                      selectedDate={selectedDate}
                      admin={false}
                      isMobile={false}
                    />
                  )}

                  <OverlayScrollbarsComponent
                    options={{ scrollbars: { autoHide: "scroll", autoHideDelay: 300 } }}
                    style={{ maxHeight: "65dvh", width: "100%" }}
                  >
                    <div className={styles.adminTaskList}>
                      {orderedTasks.length > 0 ? (
                        orderedTasks.map((t) => (
                          <TaskCard
                            key={t.id}
                            task={t}
                            onSuccess={handleTaskSuccess}
                            admin
                            allUsers={users}
                            allTasks={allTasks}
                            selectedDate={selectedDate}
                            empleado={empleado}
                            isMobile={true}
                            isModal={true}
                          />
                        ))
                      ) : (
                        <div className={styles.noTaskContainer}>
                          <img src={foldersImg} alt="No tasks" className={styles.noTaskImage} />
                          <p className={styles.noTaskText}>Este usuario no tiene tareas asignadas.</p>
                        </div>
                      )}
                    </div>
                  </OverlayScrollbarsComponent>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminMobile;
